/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/SaveTestModal.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                         |
 * |---|------------------------|-------------------|------------------|--------------------------------------|
 * | 1 | 2026-06-18 (Phase 3)   | Claude Sonnet 4.6 | Phase 3 Save     | Created — Save Test modal dialog     |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<SaveTestModal />)
 *
 * PROPS / EXPORTS
 * ---------------
 * SaveTestModalProps:
 *   steps        — current recorder steps to save
 *   existingId   — if set, this is an update (Save) not a create (Save As)
 *   existingName — pre-filled name when updating
 *   onSaved      — callback(tc) after successful save
 *   onCancel     — callback when user dismisses
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. If no suites exist, the modal shows a "create a suite first" message
 *    with an inline "New Suite" mini-form.
 * 2. The modal is rendered as a fixed overlay — it traps keyboard focus via
 *    autoFocus on the name input.
 * 3. Save is disabled when name is empty or no suite is selected.
 *
 * =============================================================================
 */

import { useState, useEffect } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db, saveTestCase, createSuite } from "../db/index.js";
import { generateId, IR_VERSION } from "@mimiq/ir";
import type { IRStep, TestIR } from "@mimiq/ir";
import type { TestCase } from "../db/schema.js";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SaveTestModalProps {
  steps: readonly IRStep[];
  existingId?: string;
  existingName?: string;
  existingSuiteId?: string;
  existingTags?: string[];
  onSaved: (tc: TestCase) => void;
  onCancel: () => void;
}

// ── Root component ────────────────────────────────────────────────────────────

export function SaveTestModal({
  steps, existingId, existingName, existingSuiteId, existingTags, onSaved, onCancel,
}: SaveTestModalProps) {
  const suites     = useLiveQuery(() => db.suites.orderBy("name").toArray(), []) ?? [];
  const allTestCases = useLiveQuery(() => db.testCases.toArray(), []) ?? [];

  const [name, setName] = useState(existingName ?? "");
  const [suiteId, setSuiteId] = useState(existingSuiteId ?? "");
  const [tags, setTags] = useState<string[]>(existingTags ?? []);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newSuiteName, setNewSuiteName] = useState("");
  const [creatingSuite, setCreatingSuite] = useState(false);

  // Collect all unique tags from all test cases for autocomplete
  const allTags = [...new Set(allTestCases.flatMap((tc) => tc.tags))].sort();

  // Auto-select the first suite when suites load (if none selected)
  useEffect(() => {
    if (!suiteId && suites.length > 0) {
      setSuiteId(suites[0]!.id);
    }
  }, [suites, suiteId]);

  async function handleSave() {
    if (!name.trim()) { setError("Test name is required."); return; }
    if (!suiteId) { setError("Please select or create a suite."); return; }
    setSaving(true);
    setError("");
    try {
      const launchStep = steps.find((s) => s.type === "launchApp");
      const appPkg = launchStep?.type === "launchApp" ? launchStep.packageName : undefined;
      const ir: TestIR = {
        irVersion: IR_VERSION,
        meta: { id: existingId ?? generateId(), name: name.trim(), tags: [...tags], createdBy: "studio-recorder" },
        config: {
          targets: ["android"],
          ...(appPkg ? { app: { packageName: appPkg } } : {}),
          env: {},
          dataSources: {},
          interruptionPolicy: { permissions: "allowAll", crashOnAnr: true },
          healing: { maxTierUsed: 1, minConfidence: 0.75, mode: "suggest" },
          timeouts: { step: 15000 },
        },
        flows: [{ id: generateId(), name: "Flow 1", entry: true, steps: [...steps] }],
        hooks: { beforeEach: [], afterEach: [] },
      };
      const tc = await saveTestCase({ id: existingId, suiteId, name: name.trim(), tags: [...tags], ir });
      onSaved(tc);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateSuite() {
    if (!newSuiteName.trim()) return;
    const s = await createSuite(newSuiteName.trim());
    setSuiteId(s.id);
    setNewSuiteName("");
    setCreatingSuite(false);
  }

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal-box">
        <div className="modal-header">
          <strong>{existingId ? "Save Test" : "Save Test As"}</strong>
          <button className="modal-close" onClick={onCancel}>✕</button>
        </div>

        <div className="modal-body">
          {/* Test name */}
          <div className="modal-field">
            <label className="modal-label">Test name</label>
            <input
              className="modal-input"
              placeholder="e.g. Happy path login"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void handleSave(); if (e.key === "Escape") onCancel(); }}
              autoFocus
            />
          </div>

          {/* Suite selection */}
          <div className="modal-field">
            <label className="modal-label">Suite / folder</label>
            {suites.length === 0 ? (
              <p className="modal-hint">No suites yet — create one below.</p>
            ) : (
              <select
                className="modal-select"
                value={suiteId}
                onChange={(e) => setSuiteId(e.target.value)}
              >
                <option value="">— select a suite —</option>
                {suites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Inline new-suite creator */}
          {creatingSuite ? (
            <div className="modal-new-suite">
              <input
                className="modal-input"
                placeholder="New suite name…"
                value={newSuiteName}
                onChange={(e) => setNewSuiteName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleCreateSuite();
                  if (e.key === "Escape") { setCreatingSuite(false); setNewSuiteName(""); }
                }}
                autoFocus
              />
              <button className="btn btn--primary" onClick={() => void handleCreateSuite()}>Create</button>
              <button className="btn" onClick={() => { setCreatingSuite(false); setNewSuiteName(""); }}>Cancel</button>
            </div>
          ) : (
            <button className="modal-new-suite-btn" onClick={() => setCreatingSuite(true)}>
              + New suite
            </button>
          )}

          {/* Tags */}
          <div className="modal-field">
            <label className="modal-label">Tags <span style={{ fontWeight: 400, color: "var(--color-text-muted)", fontSize: "0.75rem" }}>(optional — e.g. smoke, sanity, login)</span></label>
            <div className="tag-input-wrap">
              {tags.map((t) => (
                <span key={t} className="tcl-tag tcl-tag--editable">
                  {t}
                  <button className="tcl-tag-remove" onClick={() => setTags(tags.filter((x) => x !== t))} title="Remove tag">×</button>
                </span>
              ))}
              <div style={{ position: "relative", flex: 1, minWidth: 120 }}>
                <input
                  className="modal-input modal-input--tag"
                  placeholder={tags.length === 0 ? "Add tags…" : ""}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
                      e.preventDefault();
                      const clean = tagInput.trim().toLowerCase().replace(/,/g, "");
                      if (clean && !tags.includes(clean)) setTags([...tags, clean]);
                      setTagInput("");
                    }
                    if (e.key === "Backspace" && !tagInput && tags.length) setTags(tags.slice(0, -1));
                  }}
                />
                {tagInput.trim() && (
                  <ul className="tag-input-dropdown">
                    {allTags.filter((t) => !tags.includes(t) && t.includes(tagInput.toLowerCase())).slice(0, 8).map((t) => (
                      <li key={t}>
                        <button className="tag-input-dropdown-item" onMouseDown={(e) => { e.preventDefault(); if (!tags.includes(t)) setTags([...tags, t]); setTagInput(""); }}>
                          {t}
                        </button>
                      </li>
                    ))}
                    {tagInput.trim() && !allTags.includes(tagInput.trim().toLowerCase()) && (
                      <li>
                        <button className="tag-input-dropdown-item tag-input-dropdown-item--new"
                          onMouseDown={(e) => { e.preventDefault(); const c = tagInput.trim().toLowerCase(); if (c && !tags.includes(c)) setTags([...tags, c]); setTagInput(""); }}>
                          + Create "{tagInput.trim().toLowerCase()}"
                        </button>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
            <p style={{ fontSize: "0.72rem", color: "var(--color-text-muted)", marginTop: 4 }}>Press Enter or comma to add · Backspace removes last tag</p>
          </div>

          {error && <p className="modal-error">{error}</p>}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn--primary"
            disabled={saving || !name.trim() || !suiteId}
            onClick={() => void handleSave()}
          >
            {saving ? "Saving…" : existingId ? "Save" : "Save As"}
          </button>
          <button className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
